import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import Avatar from "@/components/Avatar";
import TrustBadges from "@/components/TrustBadges";
import { Badge, serviceMeta } from "@/lib/ui";
import { Calendar, MapPin, ShieldCheck, Clock, PhoneCall, LogOut, Heart } from "lucide-react";
import FamilyAlertPrefs from "@/components/FamilyAlertPrefs";
import FamilyDraftForm from "@/components/FamilyDraftForm";
import VisitStatus from "@/components/VisitStatus";
import StatementsList from "@/components/StatementsList";

const when = (v) => v.scheduled_at ? new Date(v.scheduled_at).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : v.scheduled_date;

function Safety({ v }) {
  if (v.safety_status === "safe") return <span data-testid="fv-safe" className="inline-flex items-center gap-1 text-[#1F5F4F] font-semibold"><ShieldCheck className="w-5 h-5" /> Checked in safe {v.safe_at && `at ${new Date(v.safe_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}</span>;
  if (v.safety_status === "alerted") return <span data-testid="fv-alerted" className="inline-flex items-center gap-1 text-[#A9741A] font-semibold"><PhoneCall className="w-5 h-5" /> No check-in yet — please call</span>;
  if (v.safety_status === "pending") return <span data-testid="fv-pending" className="inline-flex items-center gap-1 text-slate-600 font-medium"><Clock className="w-5 h-5" /> Waiting for check-in</span>;
  return null;
}

function Visit({ v, past }) {
  const meta = serviceMeta(v.service_type);
  return (
    <div data-testid={`fv-visit-${v.id}`} className="bg-white border border-[#E2D9CE] rounded-2xl p-5 flex flex-col sm:flex-row gap-4">
      <span className="w-12 h-12 rounded-xl bg-[#FBEBE4] flex items-center justify-center shrink-0"><meta.icon className="w-6 h-6 text-[#D96B43]" /></span>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2"><p className="text-xl font-semibold text-slate-900">{v.title}</p><Badge color="slate">{v.status.replace("_", " ")}</Badge></div>
        <p className="flex items-center gap-2 text-slate-700 mt-1"><Calendar className="w-4 h-4 text-[#2E7D6B]" /> {when(v)}</p>
        <p className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-[#2E7D6B]" /> {v.location}</p>
        <div className="mt-2"><VisitStatus request={v} compact /></div>
        {past && <div className="mt-2"><Safety v={v} /></div>}
      </div>
      {v.helper?.name && (
        <div className="flex items-center gap-3 sm:flex-col sm:items-center sm:text-center sm:w-36">
          <Avatar user={v.helper} size="md" />
          <div><p className="font-semibold text-slate-900">{v.helper.name}</p><div className="flex flex-wrap gap-1 justify-center mt-1"><TrustBadges user={v.helper} /></div></div>
        </div>
      )}
    </div>
  );
}

export default function FamilyView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("kindred_family_token");

  useEffect(() => {
    if (!token) { setError("You need a family link to view this page."); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const load = () => axios.get(`${API}/family/overview`, { headers: { "X-Family-Token": token } })
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Could not load family view"));

  const logout = async () => {
    await axios.post(`${API}/family/logout`, {}, { headers: { "X-Family-Token": token } }).catch(() => {});
    localStorage.removeItem("kindred_family_token");
    localStorage.removeItem("kindred_family_name");
    navigate("/");
  };

  if (error) return (
    <div data-testid="family-view-error" className="max-w-xl mx-auto px-4 py-20 text-center">
      <p className="text-xl font-semibold text-slate-800">{error}</p>
      <p className="text-slate-600 mt-2">Ask your family member to send a new link from their Kindred Profile.</p>
    </div>
  );
  if (!data) return <div className="max-w-4xl mx-auto px-4 py-16 text-lg">Loading…</div>;

  return (
    <div data-testid="family-view" className="max-w-4xl mx-auto px-4 py-10">
      <div className="bg-[#2E7D6B] text-white rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <Avatar user={data.seeker} size="xl" color="bg-[#D96B43]" />
        <div className="flex-1">
          <span className="inline-flex items-center gap-2 bg-white/15 px-3 py-1 rounded-full text-sm font-semibold"><Heart className="w-4 h-4" fill="white" /> Family View · read only</span>
          <h1 className="text-3xl font-display font-bold mt-2">{data.seeker.name}</h1>
          <p className="text-white/85 mt-1">Hello {data.contact_name || "there"} — here's what's coming up and how recent visits went.</p>
        </div>
        <button data-testid="family-logout-btn" onClick={logout} className="flex items-center gap-2 bg-white/15 hover:bg-white/25 px-4 min-h-[48px] rounded-xl font-semibold"><LogOut className="w-5 h-5" /> Sign out</button>
      </div>

      <h2 className="text-2xl font-display font-bold text-slate-900 mt-10 mb-4">Upcoming visits</h2>
      {data.upcoming.length === 0 ? <p data-testid="fv-upcoming-empty" className="text-slate-600 bg-white border border-[#E2D9CE] rounded-2xl p-6">No helper visits booked right now.</p>
        : <div className="space-y-4">{data.upcoming.map((v) => <Visit key={v.id} v={v} />)}</div>}

      <h2 className="text-2xl font-display font-bold text-slate-900 mt-10 mb-4">Recent visits & safety check-ins <span className="text-slate-500 text-base font-normal">(last 30 days)</span></h2>
      {data.recent.length === 0 ? <p data-testid="fv-recent-empty" className="text-slate-600 bg-white border border-[#E2D9CE] rounded-2xl p-6">No completed visits in the last 30 days.</p>
        : <div className="space-y-4">{data.recent.map((v) => <Visit key={v.id} v={v} past />)}</div>}

      <FamilyDraftForm token={token} seekerName={data.seeker.name} drafts={data.drafts || []} onCreated={load} />
      <FamilyAlertPrefs prefs={data.alert_prefs} token={token} onSaved={(p) => setData({ ...data, alert_prefs: p })} />
      <StatementsList family familyToken={token} />

      <p className="text-sm text-slate-500 mt-10">Access expires {new Date(data.session_expires_at).toLocaleDateString()}. {data.seeker.name.split(" ")[0]} can turn off sharing any time.</p>
    </div>
  );
}
